import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { format, parse, isValid } from "date-fns";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { Heart, Mail, Lock, CalendarIcon, KeyRound } from "lucide-react";
import FeedbackFooter from "@/components/FeedbackFooter";

export default function ForgotPassword() {
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [dob, setDob] = useState(""); // YYYY-MM-DD
    const [newPw, setNewPw] = useState("");
    const [confirmPw, setConfirmPw] = useState("");
    const [dateOpen, setDateOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const dobDate = dob ? parse(dob, "yyyy-MM-dd", new Date()) : null;

    const submit = async (e) => {
        e.preventDefault();
        if (!dob) {
            toast.error("Please enter your date of birth");
            return;
        }
        if (newPw.length < 6) {
            toast.error("New password must be at least 6 characters");
            return;
        }
        if (newPw !== confirmPw) {
            toast.error("New passwords do not match");
            return;
        }
        setLoading(true);
        try {
            await api.post("/auth/reset-password", { email, dob, new_password: newPw });
            toast.success("Password reset. Please sign in with your new password.");
            navigate("/login", { replace: true });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not reset password");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen relative overflow-hidden bg-background flex items-center justify-center p-6">
            <div className="blob blob-peach w-[500px] h-[500px] -top-40 -left-40" />
            <div className="blob blob-cream w-[500px] h-[500px] -bottom-40 -right-40" />

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: "easeOut" }}
                className="relative z-10 w-full max-w-md"
            >
                <Link to="/" className="flex items-center gap-2 mb-10" data-testid="forgot-brand">
                    <div className="w-10 h-10 rounded-2xl bg-primary flex items-center justify-center shadow-soft">
                        <Heart className="w-5 h-5 text-primary-foreground" strokeWidth={2} fill="currentColor" />
                    </div>
                    <span className="font-heading text-2xl font-[800] tracking-tight">CheerUp!</span>
                </Link>

                <div className="bg-card rounded-3xl p-8 sm:p-10 shadow-soft-lg border border-border">
                    <div className="flex items-center gap-2 mb-2">
                        <KeyRound className="w-5 h-5 text-primary" strokeWidth={1.5} />
                        <h1 className="font-heading text-3xl font-[800] tracking-tight">Reset your password</h1>
                    </div>
                    <p className="text-muted-foreground mb-8">
                        Confirm your email and date of birth on file, and choose a new password.
                    </p>

                    <form onSubmit={submit} className="space-y-5">
                        <div>
                            <Label htmlFor="email" className="text-sm font-medium mb-2 block">Email</Label>
                            <div className="relative">
                                <Mail className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" strokeWidth={1.5} />
                                <Input
                                    id="email" type="email" required
                                    value={email} onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@example.com"
                                    className="h-12 pl-11 rounded-2xl bg-background border-border"
                                    data-testid="forgot-email-input"
                                />
                            </div>
                        </div>

                        <div>
                            <Label className="text-sm font-medium mb-2 block">Date of birth</Label>
                            <Popover open={dateOpen} onOpenChange={setDateOpen}>
                                <PopoverTrigger asChild>
                                    <Button
                                        type="button" variant="outline"
                                        className="w-full h-12 rounded-2xl bg-background justify-start font-normal border-border"
                                        data-testid="forgot-dob-trigger"
                                    >
                                        <CalendarIcon className="w-4 h-4 mr-2 text-muted-foreground" strokeWidth={1.5} />
                                        {dobDate && isValid(dobDate) ? format(dobDate, "PPP") : "Choose your date of birth"}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="p-0 rounded-2xl" align="start">
                                    <Calendar
                                        mode="single"
                                        selected={dobDate || undefined}
                                        onSelect={(d) => {
                                            if (d) { setDob(format(d, "yyyy-MM-dd")); setDateOpen(false); }
                                        }}
                                        captionLayout="dropdown-buttons"
                                        fromYear={1900}
                                        toYear={new Date().getFullYear()}
                                        defaultMonth={dobDate || new Date(2000, 0)}
                                        disabled={(d) => d > new Date() || d < new Date("1900-01-01")}
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>

                        <div>
                            <Label htmlFor="new-pw" className="text-sm font-medium mb-2 block">New password</Label>
                            <div className="relative">
                                <Lock className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" strokeWidth={1.5} />
                                <Input
                                    id="new-pw" type="password" required autoComplete="new-password"
                                    value={newPw} onChange={(e) => setNewPw(e.target.value)}
                                    placeholder="At least 6 characters"
                                    className="h-12 pl-11 rounded-2xl bg-background border-border"
                                    data-testid="forgot-new-password-input"
                                />
                            </div>
                        </div>

                        <div>
                            <Label htmlFor="confirm-pw" className="text-sm font-medium mb-2 block">Confirm new password</Label>
                            <div className="relative">
                                <Lock className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" strokeWidth={1.5} />
                                <Input
                                    id="confirm-pw" type="password" required autoComplete="new-password"
                                    value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)}
                                    placeholder="Repeat new password"
                                    className="h-12 pl-11 rounded-2xl bg-background border-border"
                                    data-testid="forgot-confirm-password-input"
                                />
                            </div>
                        </div>

                        <Button
                            type="submit" disabled={loading}
                            className="w-full h-12 rounded-2xl font-medium shadow-soft"
                            data-testid="forgot-submit-btn"
                        >
                            {loading ? "Resetting…" : "Reset password"}
                        </Button>
                    </form>

                    <p className="text-center text-sm text-muted-foreground mt-6">
                        Remembered it?{" "}
                        <Link to="/login" className="text-primary font-medium hover:underline" data-testid="forgot-back-to-login">
                            Back to sign in
                        </Link>
                    </p>
                    <p className="text-center text-xs text-muted-foreground mt-4">
                        DOB reset is only available for accounts that added a date of birth in Profile settings. If yours doesn't have one on file, please contact <a href="mailto:query@cheerup-ai.com" className="text-primary underline">query@cheerup-ai.com</a>.
                    </p>
                </div>
                <FeedbackFooter />
            </motion.div>
        </div>
    );
}
